import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { useCardController } from "@datanac/datanac-mui-toolkit";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import { Box } from "@mui/material";
import Button from "@mui/material/Button";
import { AppContext } from "App";
import BudgetSummaryCard from "components/Budget/components/BudgetSummaryCard";
import BudgetSummaryDataTable from "components/Budget/components/BudgetSummaryDataTable";
import { BUDGET_STATUS_ACTIVE, BUDGET_STATUS_DRAFT } from "components/Budget/helpers";
import EmptyCard from "components/Cards/EmptyCard";
import { WORKSPACE_VIEW_CARD, WORKSPACE_VIEW_TABLE } from "components/Workspaces/helpers";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import { useSnackbar } from "notistack";

import { UsersApiHelper } from "../../api/ApiHelper";
import { useChatAssistantAttachments } from "../../contexts/useChatAssistantAttachments";
import { BudgetIcon } from "../Icons/Icons";

export default function BudgetListWorkspace() {
  const { globalState, globalDispatch } = useContext(AppContext);
  const { addAttachments } = useChatAssistantAttachments();

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);

  const CARD_GRID_KEY = "budget_list";
  const [workspaceMode, setWorkspaceMode] = useState(null);

  const { state: budgetCards, actions: budgetActions } = useCardController();
  const [currentYear, setCurrentYear] = useState(globalState?.crop_year);
  const [isLoadingCopy, setIsLoadingCopy] = useState(false);

  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "crop_year",
        value: currentYear,
      },
    });
  }, [currentYear]);

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const loadBudgets = async () => {
    try {
      const budgets = (
        await UsersApiHelper.selectBudgets(
          {
            year: currentYear,
            is_active: true,
          },
          globalState
        )
      )?.sort((a, b) => a.name?.localeCompare(b.name));
      budgetActions.replace(budgets);

      addAttachments([
        {
          name: `Budgets for ${currentYear}`,
          content: budgets,
        },
      ]);
    } catch (err) {
      budgetActions.replace([]);
      handleError(err);
    }
  };

  useEffect(() => {
    if (currentYear && globalState?.producer_token) {
      setIsLoading(true);
      loadBudgets().then(() => {
        setIsLoading(false);
      });
    }
  }, [globalState?.producer_token, currentYear]);

  const handleAddNewClick = () => {
    navigate(`/budget/0`);
  };

  const handleBudgetCardOnChange = async (_newBudget) => {
    if (_newBudget) {
      try {
        setIsLoading(true);
        const updatePromises = [];

        if (_newBudget.is_active === false) {
          const remainingBudgets = budgetCards.filter((b) => b.id !== _newBudget.id);
          if (remainingBudgets.length === 1) {
            const budgetToActivate = { ...remainingBudgets[0], status: BUDGET_STATUS_ACTIVE };
            updatePromises.push(UsersApiHelper.updateBudget(budgetToActivate, globalState));
          }
        }

        if (_newBudget.status === BUDGET_STATUS_ACTIVE && _newBudget.is_active !== false) {
          budgetCards
            .filter((b) => b?.status === BUDGET_STATUS_ACTIVE && b?.id !== _newBudget?.id)
            .forEach((b) => {
              const updated = { ...b, status: BUDGET_STATUS_DRAFT };
              updatePromises.push(UsersApiHelper.updateBudget(updated, globalState));
            });
        }

        // Update the current budget
        updatePromises.push(UsersApiHelper.updateBudget(_newBudget, globalState));

        await Promise.all(updatePromises);

        // Reload budgets to ensure UI is in sync
        await loadBudgets();
      } catch (error) {
        handleError(error);
      } finally {
        setIsLoadingCopy(false);
        setIsLoading(false);
      }
    } else {
      try {
        setIsLoading(true);
        await loadBudgets();
      } catch (error) {
        handleError(error);
      } finally {
        setIsLoadingCopy(false);
        setIsLoading(false);
      }
    }
  };

  // ---

  return (
    <>
      <WorkspaceHeader
        title="Farm Budgets"
        category={{
          title: "Budgets",
          icon: <BudgetIcon />,
        }}
      />

      <WorkspacePageHeader
        currentYear={currentYear}
        onYearChange={setCurrentYear}
        viewMode={workspaceMode}
        onViewModeChange={setWorkspaceMode}
        cardGridKey={CARD_GRID_KEY}
        isLoading={isLoading || isLoadingCopy}
      />

      {workspaceMode === WORKSPACE_VIEW_CARD && (
        <Box className="cardContainer">
          {budgetCards.map((currentBudget, index) => (
            <BudgetSummaryCard
              key={index}
              budget={currentBudget}
              onChange={handleBudgetCardOnChange}
              isLoading={isLoading}
              setIsLoadingCopy={setIsLoadingCopy}
            />
          ))}

          {!budgetCards?.length && (
            <EmptyCard text="No Budgets Defined" secondaryText="Get started by adding a budget" isLoading={isLoading} />
          )}
        </Box>
      )}

      {workspaceMode === WORKSPACE_VIEW_TABLE && (
        <BudgetSummaryDataTable
          budgetCards={budgetCards}
          isLoading={isLoading}
          setIsLoadingCopy={setIsLoadingCopy}
          onChange={handleBudgetCardOnChange}
        />
      )}
      <WorkspaceActionBar
        content={
          <>
            <Box>
              <Button variant="contained" sx={{ width: "100%" }} onClick={handleAddNewClick}>
                <AddCircleOutline sx={{ mr: 1 }} />
                New Budget
              </Button>
            </Box>
            {/* <Box>
          <Button
            variant="contained"
            sx={{ width: '100%' }}
            onClick={toggleOnlineHelpOpen(true)}
          >
            <QuestionMarkOutlinedIcon sx={{ fontSize: 20 }} />
          </Button>
        </Box> */}
          </>
        }
      />

      {/* <BudgetOnlineHelp isOpen={isOnlineHelpOpen} onClose={handleCloseOnlineHelp} /> */}
    </>
  );
}
